import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { 
  Heart, Users, Briefcase, GraduationCap, Home, Baby, 
  Coffee, Wine, Cigarette, Dumbbell, Book, Plane, Music,
  Target, Star, CheckCircle, ArrowRight, ArrowLeft
} from 'lucide-react';

const DetailedProfileQuestions = ({ onComplete, existingData = {} }) => {
  const [currentSection, setCurrentSection] = useState(0);
  const [answers, setAnswers] = useState({
    // Relationship Goals
    relationshipGoals: [],
    seriousnessLevel: '',
    familyPlans: '',
    timelineExpectations: '',
    
    // Lifestyle
    socialStyle: '',
    activityLevel: '',
    workLifeBalance: '',
    travelStyle: '',
    livingStyle: '',
    
    // Values & Beliefs
    politicalViews: '',
    religiousViews: '',
    importantValues: [],
    dealbreakers: [],
    
    // Personal Habits
    drinkingHabits: '',
    smokingHabits: '',
    fitnessLevel: '',
    dietaryPreferences: [],
    
    // Career & Education
    educationLevel: '',
    careerField: '',
    careerImportance: '',
    incomeComfort: '',
    
    // Communication & Personality
    communicationStyle: '',
    conflictResolution: '',
    personalityType: '',
    loveLanguage: '',
    
    // Future Plans
    longTermGoals: [],
    idealLifestyle: '',
    whereToLive: '',
    
    ...existingData
  });

  const sections = [
    {
      id: 'relationship-goals',
      title: 'Relationship Goals',
      subtitle: 'What are you looking for?',
      icon: Heart,
      questions: [
        {
          key: 'relationshipGoals',
          type: 'multi-select',
          question: 'What are you looking for? (Select all that apply)',
          options: [
            'Casual dating',
            'Serious relationship',
            'Marriage',
            'Life partner',
            'Companionship',
            'Someone to explore the city with',
            'Travel partner',
            'Intellectual connection'
          ]
        },
        {
          key: 'seriousnessLevel',
          type: 'single-select',
          question: 'How serious are you about finding a relationship?',
          options: [
            'Just exploring and having fun',
            'Open to something serious if it develops naturally',
            'Actively looking for a committed relationship',
            'Ready for marriage within 1-2 years',
            'Looking for my soulmate'
          ]
        },
        {
          key: 'familyPlans',
          type: 'single-select',
          question: 'What are your family plans?',
          options: [
            'Want children someday',
            'Want children soon (within 2 years)',
            "Don't want children",
            'Have children and want more',
            'Have children and don\'t want more',
            'Open to either',
            'Prefer not to say'
          ]
        },
        {
          key: 'timelineExpectations',
          type: 'single-select',
          question: 'What\'s your ideal timeline for a relationship?',
          options: [
            'Take it very slow (6+ months before exclusivity)',
            'Moderate pace (2-6 months to know if it\'s serious)',
            'Relatively quick (1-3 months to exclusivity)',
            'When you know, you know (could be exclusive quickly)',
            'No timeline - go with the flow'
          ]
        }
      ]
    },
    {
      id: 'lifestyle',
      title: 'Lifestyle',
      subtitle: 'How do you live your life?',
      icon: Coffee,
      questions: [
        {
          key: 'socialStyle',
          type: 'single-select',
          question: 'How would you describe your social style?',
          options: [
            'Life of the party - love big gatherings',
            'Social butterfly - enjoy meeting new people',
            'Balanced - mix of social and quiet time',
            'Small circles - prefer intimate gatherings',
            'Homebody - quiet nights are my favorite',
            'Selective socializer - quality over quantity'
          ]
        },
        {
          key: 'activityLevel',
          type: 'single-select',
          question: 'What\'s your activity level?',
          options: [
            'High energy - always on the go',
            'Active - regular exercise and outdoor activities',
            'Moderately active - some exercise, some relaxation',
            'Low key - prefer calm, peaceful activities',
            'Varies by mood and season'
          ]
        },
        {
          key: 'workLifeBalance',
          type: 'single-select',
          question: 'How do you approach work-life balance?',
          options: [
            'Work to live - personal life comes first',
            'Balanced approach - equal focus on both',
            'Live to work - career is a top priority',
            'Seasonal - sometimes work-focused, sometimes life-focused',
            'Still figuring it out'
          ]
        },
        {
          key: 'travelStyle',
          type: 'single-select',
          question: 'What\'s your travel style?',
          options: [
            'Adventure seeker - backpacking and exploration',
            'Luxury traveler - comfort and fine experiences',
            'Cultural explorer - museums, history, local experiences',
            'Relaxation focused - beaches and spas',
            'Budget conscious - hostels and local transportation',
            'Homebody - prefer staycations',
            'Business traveler - travel for work frequently'
          ]
        }
      ]
    },
    {
      id: 'values',
      title: 'Values & Beliefs',
      subtitle: 'What matters most to you?',
      icon: Star,
      questions: [
        {
          key: 'importantValues',
          type: 'multi-select',
          question: 'What values are most important to you? (Select up to 6)',
          maxSelections: 6,
          options: [
            'Honesty and transparency',
            'Loyalty and commitment',
            'Kindness and compassion',
            'Ambition and drive',
            'Family and relationships',
            'Personal growth',
            'Financial security',
            'Adventure and spontaneity',
            'Creativity and self-expression',
            'Health and wellness',
            'Spirituality and faith',
            'Social justice and equality',
            'Environmental consciousness',
            'Intellectual curiosity',
            'Humor and fun',
            'Independence and freedom'
          ]
        },
        {
          key: 'politicalViews',
          type: 'single-select',
          question: 'How would you describe your political views?',
          options: [
            'Very liberal',
            'Liberal',
            'Moderate/Centrist',
            'Conservative',
            'Very conservative',
            'Libertarian',
            'Not political',
            'Prefer not to say'
          ]
        },
        {
          key: 'religiousViews',
          type: 'single-select',
          question: 'What best describes your religious/spiritual beliefs?',
          options: [
            'Very religious - faith is central to my life',
            'Somewhat religious - faith is important',
            'Spiritual but not religious',
            'Agnostic - unsure about religious matters',
            'Atheist - do not believe in God',
            'Not religious or spiritual',
            'Prefer not to say'
          ]
        },
        {
          key: 'dealbreakers',
          type: 'multi-select',
          question: 'What are absolute dealbreakers for you? (Select all that apply)',
          options: [
            'Smoking',
            'Heavy drinking',
            'Drug use',
            'Different political views',
            'Different religious views',
            'Doesn\'t want children (if I do)',
            'Wants children (if I don\'t)',
            'Lives too far away',
            'Different career ambitions',
            'Poor communication',
            'Dishonesty',
            'Lack of ambition',
            'Different social styles',
            'Financial irresponsibility',
            'Different family values'
          ]
        }
      ]
    },
    {
      id: 'habits',
      title: 'Personal Habits',
      subtitle: 'Your lifestyle choices',
      icon: Dumbbell,
      questions: [
        {
          key: 'drinkingHabits',
          type: 'single-select',
          question: 'How often do you drink alcohol?',
          options: [
            'Never',
            'Rarely (special occasions only)',
            'Occasionally (1-2 times per month)',
            'Socially (1-2 times per week)',
            'Regularly (3-4 times per week)',
            'Daily',
            'Prefer not to say'
          ]
        },
        {
          key: 'smokingHabits',
          type: 'single-select',
          question: 'Do you smoke?',
          options: [
            'Never',
            'Socially',
            'Occasionally',
            'Regularly',
            'Trying to quit',
            'Vape only',
            'Prefer not to say'
          ]
        },
        {
          key: 'fitnessLevel',
          type: 'single-select',
          question: 'How would you describe your fitness level?',
          options: [
            'Very fit - exercise is a major part of my life',
            'Active - regular exercise routine',
            'Moderately active - exercise occasionally',
            'Trying to be more active',
            'Not very active',
            'Prefer mental health activities over physical'
          ]
        },
        {
          key: 'dietaryPreferences',
          type: 'multi-select',
          question: 'Any dietary preferences? (Select all that apply)',
          options: [
            'None - eat everything',
            'Vegetarian',
            'Vegan',
            'Pescatarian',
            'Keto',
            'Paleo',
            'Gluten-free',
            'Dairy-free',
            'Halal',
            'Kosher',
            'Health-conscious eater',
            'Food adventurer'
          ]
        }
      ]
    },
    {
      id: 'career',
      title: 'Career & Goals', 
      subtitle: 'Your professional life',
      icon: Briefcase,
      questions: [
        {
          key: 'educationLevel',
          type: 'single-select',
          question: 'What\'s your highest level of education?',
          options: [
            'High school',
            'Some college',
            'Bachelor\'s degree',
            'Master\'s degree',
            'Doctoral degree',
            'Professional degree (JD, MD, etc.)',
            'Trade school/certification',
            'Self-educated',
            'Prefer not to say'
          ]
        },
        {
          key: 'careerField',
          type: 'single-select',
          question: 'What field do you work in?',
          options: [
            'Technology',
            'Healthcare',
            'Education',
            'Finance',
            'Arts/Entertainment',
            'Business/Consulting',
            'Law',
            'Engineering',
            'Marketing/Communications',
            'Non-profit/Social services',
            'Government',
            'Retail/Service',
            'Entrepreneurship',
            'Student',
            'Between jobs',
            'Retired',
            'Other'
          ]
        },
        {
          key: 'careerImportance',
          type: 'single-select',
          question: 'How important is career success to you?',
          options: [
            'Extremely important - career is my top priority',
            'Very important - want to achieve high levels of success',
            'Moderately important - want a stable, fulfilling career',
            'Somewhat important - work to support my lifestyle',
            'Not very important - other things matter more',
            'Currently focusing on other priorities'
          ]
        }
      ]
    },
    {
      id: 'personality',
      title: 'Personality & Communication',
      subtitle: 'How you connect with others',
      icon: Users,
      questions: [
        {
          key: 'communicationStyle',
          type: 'single-select',
          question: 'How do you prefer to communicate?',
          options: [
            'Direct and straightforward',
            'Thoughtful and diplomatic', 
            'Warm and expressive',
            'Quiet and reserved',
            'Humorous and lighthearted',
            'Deep and philosophical',
            'Practical and solution-focused'
          ]
        },
        {
          key: 'conflictResolution',
          type: 'single-select',
          question: 'How do you handle disagreements?',
          options: [
            'Address issues immediately and directly',
            'Take time to think before discussing',
            'Try to find compromise and middle ground',
            'Avoid conflict when possible',
            'Need to talk it through completely',
            'Prefer to cool off first, then discuss',
            'Look for win-win solutions'
          ]
        },
        {
          key: 'loveLanguage',
          type: 'single-select',
          question: 'What\'s your primary love language?',
          options: [
            'Words of affirmation',
            'Quality time',
            'Physical touch',
            'Acts of service',
            'Receiving gifts',
            'Not sure yet'
          ]
        }
      ]
    },
    {
      id: 'future',
      title: 'Future Plans',
      subtitle: 'Your vision for the future',
      icon: Target,
      questions: [
        {
          key: 'longTermGoals',
          type: 'multi-select',
          question: 'What are your long-term goals? (Select all that apply)',
          options: [
            'Build a successful career',
            'Start a family',
            'Travel the world',
            'Buy a home',
            'Start a business',
            'Pursue higher education',
            'Make a difference in my community',
            'Achieve financial independence',
            'Write a book/create art',
            'Learn new skills',
            'Live abroad',
            'Retire early'
          ]
        },
        {
          key: 'idealLifestyle',
          type: 'single-select',
          question: 'What\'s your ideal lifestyle in 5 years?',
          options: [
            'Urban professional - city apartment, career-focused',
            'Suburban family life - house with yard, community-oriented',
            'Rural/small town - peaceful, close to nature',
            'Digital nomad - location independent, travel-focused',
            'Entrepreneurial - building businesses, flexible schedule',
            'Creative lifestyle - pursuing arts, flexible income',
            'International - living abroad or frequently traveling'
          ]
        },
        {
          key: 'whereToLive',
          type: 'single-select',
          question: 'Where do you see yourself living long-term?',
          options: [
            'Same city I\'m in now',
            'Different city in the same region',
            'Different part of the country',
            'International - specific country in mind',
            'Wherever life takes me',
            'Depends on my partner\'s preferences',
            'Still figuring it out'
          ]
        }
      ]
    }
  ];

  const calculateProgress = () => {
    let totalQuestions = 0;
    let answeredQuestions = 0;

    sections.forEach(section => {
      section.questions.forEach(question => {
        totalQuestions++;
        const answer = answers[question.key];
        if (answer && (Array.isArray(answer) ? answer.length > 0 : answer !== '')) {
          answeredQuestions++;
        }
      });
    });

    return Math.round((answeredQuestions / totalQuestions) * 100);
  };

  const handleAnswer = (key, value) => {
    setAnswers(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const nextSection = () => {
    if (currentSection < sections.length - 1) {
      setCurrentSection(currentSection + 1);
    }
  };

  const prevSection = () => {
    if (currentSection > 0) {
      setCurrentSection(currentSection - 1);
    }
  };

  const handleComplete = () => {
    onComplete(answers);
  };

  const currentSectionData = sections[currentSection];
  const Icon = currentSectionData.icon;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-rose-900 p-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">
            Complete Your Profile
          </h1>
          <p className="text-gray-300 mb-4">
            Help us find your perfect matches with detailed preferences
          </p>
          <Progress value={calculateProgress()} className="w-full mb-2" />
          <p className="text-sm text-gray-400">
            {calculateProgress()}% complete • Section {currentSection + 1} of {sections.length}
          </p>
        </div>

        {/* Section Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentSection}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <Card className="backdrop-blur-xl bg-black/20 border border-white/20">
              <CardHeader className="text-center">
                <Icon className="h-12 w-12 text-rose-400 mx-auto mb-4" />
                <CardTitle className="text-2xl text-white">
                  {currentSectionData.title}
                </CardTitle>
                <p className="text-gray-300">
                  {currentSectionData.subtitle}
                </p>
              </CardHeader>
              
              <CardContent className="space-y-8">
                {currentSectionData.questions.map((question, qIndex) => (
                  <div key={question.key} className="space-y-4">
                    <h3 className="text-lg font-medium text-white">
                      {question.question}
                    </h3>
                    
                    {question.type === 'single-select' && (
                      <div className="grid gap-2">
                        {question.options.map((option, oIndex) => (
                          <button
                            key={oIndex}
                            onClick={() => handleAnswer(question.key, option)}
                            className={`p-3 rounded-lg text-left transition-all ${
                              answers[question.key] === option
                                ? 'bg-rose-500 text-white'
                                : 'bg-white/10 text-gray-300 hover:bg-white/20'
                            }`}
                          >
                            {option}
                          </button>
                        ))}
                      </div>
                    )}
                    
                    {question.type === 'multi-select' && (
                      <div className="grid gap-2">
                        {question.options.map((option, oIndex) => {
                          const currentAnswers = answers[question.key] || [];
                          const isSelected = currentAnswers.includes(option);
                          const canSelect = !question.maxSelections || 
                            currentAnswers.length < question.maxSelections || 
                            isSelected;
                          
                          return (
                            <button
                              key={oIndex}
                              disabled={!canSelect}
                              onClick={() => {
                                const current = answers[question.key] || [];
                                if (isSelected) {
                                  handleAnswer(question.key, current.filter(item => item !== option));
                                } else {
                                  handleAnswer(question.key, [...current, option]);
                                }
                              }}
                              className={`p-3 rounded-lg text-left transition-all flex items-center justify-between ${
                                isSelected
                                  ? 'bg-rose-500 text-white'
                                  : canSelect
                                    ? 'bg-white/10 text-gray-300 hover:bg-white/20'
                                    : 'bg-gray-500/20 text-gray-500 cursor-not-allowed'
                              }`}
                            >
                              {option}
                              {isSelected && <CheckCircle className="h-5 w-5" />}
                            </button>
                          );
                        })}
                        {question.maxSelections && (
                          <p className="text-sm text-gray-400 mt-2">
                            Select up to {question.maxSelections} options 
                            ({(answers[question.key] || []).length}/{question.maxSelections} selected)
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>
          </motion.div>
        </AnimatePresence>

        {/* Navigation */}
        <div className="flex justify-between items-center mt-8">
          <Button
            variant="outline"
            onClick={prevSection}
            disabled={currentSection === 0}
            className="bg-white/10 border-white/20 text-white hover:bg-white/20"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Previous
          </Button>
          
          <div className="text-center">
            <p className="text-gray-400 text-sm">
              Section {currentSection + 1} of {sections.length}
            </p>
          </div>
          
          {currentSection === sections.length - 1 ? (
            <Button
              onClick={handleComplete}
              className="bg-rose-500 hover:bg-rose-600 text-white"
              disabled={calculateProgress() < 80}
            >
              Complete Profile
              <CheckCircle className="h-4 w-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={nextSection}
              className="bg-rose-500 hover:bg-rose-600 text-white"
            >
              Next
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          )}
        </div>
        
        {calculateProgress() < 80 && currentSection === sections.length - 1 && (
          <p className="text-center text-yellow-400 text-sm mt-4">
            Complete at least 80% of questions to finish your profile
          </p>
        )}
      </div>
    </div>
  );
};

export default DetailedProfileQuestions;